'use client';

/**
 * Employee login — enterprise access (ui-ux-pro-max)
 * Gold/cream shell aligned with ClientLoginPage; labels, focus rings, ≥44px touch.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { Form, Input, Button } from 'antd';
import {
  UserOutlined,
  LockOutlined,
  SafetyOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  KeyOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import { useTheme } from '@/shared/theme';
import { AuthNavbar } from '@/domains/auth/components/AuthNavbar';
import webauthnService from '@/domains/auth/services/webauthn-service';
import { useAppDispatch, useAppSelector } from '@/shared/providers/store';
import { loginAsync, clearErrors, selectAuth, initializeAuth } from '@/shared/providers/state/authSlice';
import { logCrossDomainDiagnostics, checkMiddlewareRestrictions } from '@/infrastructure/utils/cross-domain-debugger';
import '@/shared/theme/styles/login.css';
import styles from './AuthLayout.module.css';

type AuthMode = 'password' | 'mfa' | 'passkey';

interface EmployeeLoginPageProps {
  audience?: 'employee';
}

const MODE_HINT: Record<AuthMode, string> = {
  password: 'Sign in with email and password',
  mfa: 'Sign in with email and a 6-digit authenticator code',
  passkey: 'Sign in with biometric authentication or a security key',
};

const EmployeeLoginPage: React.FC<EmployeeLoginPageProps> = ({ audience = 'employee' }) => {
  const [form] = Form.useForm();
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { isLoading, loginError, requiresMfa, mfaError, isAuthenticated } = useAppSelector(selectAuth);
  const [error, setError] = useState<string | null>(null);
  const [authMode, setAuthMode] = useState<AuthMode>('password');
  const theme = useTheme();
  const errorRef = useRef<HTMLDivElement>(null);

  const loginEndpoint = '/api/auth/employee-login';
  const redirectTo = '/dashboard';
  const mfaRedirectTo = '/auth/mfa/provisional-setup';

  useEffect(() => {
    dispatch(initializeAuth());
    logCrossDomainDiagnostics();
    const middlewareCheck = checkMiddlewareRestrictions();
    if (middlewareCheck.isBlocked) {
      console.warn('⚠️ Employee login may be blocked:', middlewareCheck.reason);
    }
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated) router.push(redirectTo);
  }, [isAuthenticated, redirectTo, router]);

  useEffect(() => {
    if (requiresMfa) router.push(mfaRedirectTo);
  }, [requiresMfa, router, mfaRedirectTo]);

  useEffect(() => {
    if (loginError) {
      setError(loginError);
      dispatch(clearErrors());
    }
    if (mfaError) {
      setError(mfaError);
      dispatch(clearErrors());
    }
  }, [loginError, mfaError, dispatch]);

  useEffect(() => {
    if (!error) return;
    const t = window.setTimeout(() => errorRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [error]);

  const handlePasskeyAuth = async (username: string) => {
    try {
      if (!window.navigator.credentials || !window.PublicKeyCredential) {
        throw new Error('Passkeys are not supported in this browser');
      }
      const isPasskeySupported =
        await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
      if (!isPasskeySupported) {
        throw new Error('Passkeys are not supported on this device');
      }
      const authOptions = await webauthnService.startAuthentication(username);
      const authResult = await webauthnService.completeAuthentication(authOptions);
      if (authResult.success) {
        const response = await fetch(loginEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: username,
            passkey_credential: { user_id: authResult.user_id },
            auth_mode: 'passkey',
            audience,
          }),
        });
        const loginResult = await response.json();
        if (loginResult.success) {
          router.push(redirectTo);
        } else {
          setError(loginResult.error || 'Passkey authentication failed');
        }
      } else {
        setError('Passkey authentication failed. Please try again.');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Passkey authentication failed';
      setError(errorMessage);
    }
  };

  const onFinish = async (values: { username: string; password?: string; mfa_token?: string }) => {
    try {
      setError(null);
      if (authMode === 'passkey') {
        await handlePasskeyAuth(values.username);
        return;
      }
      const loginRequest = {
        email: values.username,
        password: values.password || '',
        mfa_token: values.mfa_token || '',
        audience,
      };
      await dispatch(loginAsync(loginRequest)).unwrap();
    } catch (err: unknown) {
      const error = err as { status?: number; message?: string };
      if (error?.status === 401) {
        setError('Invalid credentials. Please try again.');
      } else if (error?.status === 500) {
        setError('Server error. Please try again later.');
      } else {
        setError(error?.message || 'Login failed. Please try again.');
      }
    }
  };

  const startSso = () => {
    const authentikUrl =
      process.env.NEXT_PUBLIC_AUTHENTIK_URL || 'https://authentik.taildd48ba.ts.net';
    const clientId = process.env.NEXT_PUBLIC_AUTHENTIK_CLIENT_ID;
    const redirectUri =
      process.env.NEXT_PUBLIC_AUTHENTIK_REDIRECT_URI ||
      `${window.location.origin}/api/auth/callback/authentik`;

    if (!clientId) {
      setError('SSO not configured. Please contact IT support.');
      return;
    }

    const authUrl = new URL(`${authentikUrl}/application/o/authorize/`);
    authUrl.searchParams.set('client_id', clientId);
    authUrl.searchParams.set('redirect_uri', redirectUri);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('scope', 'openid profile email groups');
    authUrl.searchParams.set('state', Math.random().toString(36).substring(7));
    window.location.href = authUrl.toString();
  };

  return (
    <div
      className={`login-page ${theme.isDark ? 'dark' : 'light'} ${styles.page}`}
      data-theme={theme.isDark ? 'dark' : 'light'}
    >
      <AuthNavbar subtitle="Employee Access" />

      <div className={styles.body}>
        <div className={styles.formContainer}>
          <div className={styles.card}>
            <div className={styles.authBezel}>
              <span className={styles.authBezelLabel}>auth · employee</span>
              <span className={styles.authBezelStatus}>ops</span>
            </div>
            <div className={styles.cardBody}>
            <header className={styles.formHeader}>
              <h1 className={styles.formTitle}>Employee sign in</h1>
              <p className={styles.formSubtitle}>
                Access platform ops, admin, and internal tools.
              </p>
            </header>

            <div className={styles.modeToggle} role="tablist" aria-label="Authentication method">
              {(
                [
                  { id: 'password', label: 'Password', icon: <LockOutlined aria-hidden /> },
                  { id: 'mfa', label: 'MFA', icon: <SafetyOutlined aria-hidden /> },
                  { id: 'passkey', label: 'Passkey', icon: <KeyOutlined aria-hidden /> },
                ] as const
              ).map((mode) => (
                <button
                  key={mode.id}
                  type="button"
                  role="tab"
                  aria-selected={authMode === mode.id}
                  onClick={() => setAuthMode(mode.id)}
                  className={`${styles.modeBtn} ${authMode === mode.id ? styles.modeBtnActive : ''}`}
                >
                  {mode.icon} {mode.label}
                </button>
              ))}
            </div>
            <p className={styles.modeHint}>{MODE_HINT[authMode]}</p>

            {error && (
              <div
                ref={errorRef}
                className={styles.errorAlert}
                role="alert"
                tabIndex={-1}
                id="employee-login-error"
              >
                <ExclamationCircleOutlined className={styles.errorIcon} aria-hidden />
                <div className={styles.errorContent}>
                  <div className={styles.errorTitle}>Could not sign in</div>
                  <div className={styles.errorText}>{error}</div>
                </div>
                <Button
                  type="text"
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={() => setError(null)}
                  className={styles.dismissBtn}
                  aria-label="Dismiss error"
                />
              </div>
            )}

            <Form
              form={form}
              name="employee-login"
              onFinish={onFinish}
              layout="vertical"
              requiredMark={false}
              className={styles.formFields}
              disabled={isLoading}
              aria-busy={isLoading}
            >
              <Form.Item
                name="username"
                htmlFor="employee-login-email"
                label={<span className={styles.fieldLabel}>Email</span>}
                rules={[
                  { required: true, message: 'Enter your email' },
                  { type: 'email', message: 'Enter a valid email' },
                ]}
                validateTrigger={['onBlur', 'onSubmit']}
              >
                <Input
                  prefix={<UserOutlined aria-hidden />}
                  type="email"
                  id="employee-login-email"
                  name="username"
                  placeholder="you@company.com"
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  inputMode="email"
                  aria-required="true"
                  aria-describedby={error ? 'employee-login-error' : undefined}
                />
              </Form.Item>

              {authMode === 'password' && (
                <Form.Item
                  name="password"
                  htmlFor="employee-login-password"
                  label={<span className={styles.fieldLabel}>Password</span>}
                  rules={[{ required: true, message: 'Enter your password' }]}
                  validateTrigger={['onBlur', 'onSubmit']}
                >
                  <Input.Password
                    prefix={<LockOutlined aria-hidden />}
                    id="employee-login-password"
                    name="password"
                    placeholder="Your password"
                    autoComplete="current-password"
                    aria-required="true"
                  />
                </Form.Item>
              )}

              {authMode === 'mfa' && (
                <Form.Item
                  name="mfa_token"
                  htmlFor="employee-login-mfa"
                  label={<span className={styles.fieldLabel}>Authenticator code</span>}
                  rules={[
                    { required: true, message: 'Enter your code' },
                    { pattern: /^\d{6}$/, message: 'Enter a 6-digit code' },
                  ]}
                  validateTrigger={['onBlur', 'onSubmit']}
                >
                  <Input
                    prefix={<SafetyOutlined aria-hidden />}
                    id="employee-login-mfa"
                    name="mfa_token"
                    placeholder="000000"
                    maxLength={6}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    className={styles.mfaInput}
                    aria-required="true"
                  />
                </Form.Item>
              )}

              {authMode === 'passkey' && (
                <div className={styles.passkeyPanel}>
                  <KeyOutlined className={styles.passkeyIcon} aria-hidden />
                  <div className={styles.passkeyTitle}>Security key</div>
                  <div className={styles.passkeyText}>
                    Use your hardware key or biometric authentication after submitting your email
                  </div>
                </div>
              )}

              <Button
                type="primary"
                htmlType="submit"
                loading={isLoading}
                block
                icon={!isLoading ? <ArrowRightOutlined /> : undefined}
                iconPlacement="end"
                className={styles.submitBtn}
              >
                {isLoading
                  ? 'Signing in…'
                  : authMode === 'passkey'
                    ? 'Authenticate with passkey'
                    : 'Sign in'}
              </Button>
            </Form>

            <div className={styles.divider} role="separator">
              <div className={styles.dividerLine} />
              <span className={styles.dividerText}>or</span>
              <div className={styles.dividerLine} />
            </div>

            <Button
              type="default"
              block
              className={styles.secondaryBtn}
              onClick={startSso}
              icon={<SafetyOutlined aria-hidden />}
            >
              Sign in with SSO
            </Button>

            <p className={styles.formFootnote}>Need help? Contact IT support.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeLoginPage;
